import gql from 'graphql-tag';
import React, {ReactNode} from 'react';

import {assert} from 'Asserts';
import {DataFrame} from 'data-frames/DataFrameTypes';
import {
  GetSubsetDataFrame,
  GetSubsetDataFrameVariables,
} from 'data-frames/graphql/__generated/GetSubsetDataFrame';
import {GET_SUBSET_DATA_FRAME_QUERY} from 'data-frames/graphql/GetDataFrame';
import {stringAccessor} from 'data-frames/util/AccessorUtils';
import {createAccessor} from 'data-frames/util/createAccessors';
import BenchlingQuery from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {graphQLArrayToREST, graphQLToREST} from 'graphql-client/declarative-translations/external/translate';
import {assertAllAccessible, isInaccessible} from 'graphql-client/InaccessibleInfo';
import {ResultSchema} from 'results/assay-schemas/AssaySchemaConstants';
import {AssayResult} from 'results/assays/AssayRecordConstants';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';

import {GetSavedResultsQuery, GetSavedResultsQueryVariables} from './__generated/GetSavedResultsQuery';
import {SAVED_RESULT_VALIDATION_OPTIONS} from './SaveToResultsStepConstants';

export const ERROR_INACCESSIBLE_RESULTS_SCHEMA_TEXT =
  'You do not have permission to access the saved Results';

export const GET_SAVED_RESULTS = gql`
  query GetSavedResultsQuery($resultIds: [StrictString!]!, $schemaId: StrictString!) @skipAudit {
    results(ids: $resultIds) {
      ... on ResultNode {
        ...ResultGraphQL
      }
      ...InaccessibleInfoGraphQL
    }
    resultSchema(id: $schemaId) {
      ... on ResultSchemaNode {
        ...ResultSchemaGraphQL
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('Result').fragment}
  ${getFragmentWrapper('ResultSchema').fragment}
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

export interface SavedResultsQueryProps {
  children: (
    totalNumSavedResults: number,
    results: Array<AssayResult>,
    resultsSchema: ResultSchema,
    refetchResults: () => void
  ) => ReactNode;
  dataFrameId: string;
  resultSchemaId: string;
  rowLimit: number;
}

export default function SavedResultsQuery({
  children,
  dataFrameId,
  resultSchemaId,
  rowLimit,
}: SavedResultsQueryProps): JSX.Element {
  return (
    <BenchlingQuery<GetSubsetDataFrame, GetSubsetDataFrameVariables>
      query={GET_SUBSET_DATA_FRAME_QUERY}
      variables={{
        dataFrameId,
        limit: rowLimit,
      }}
      renderLoading={() => <CenteredHorizontalThrobber />}
    >
      {({dataFrame: savedResultsDataFrameGQL}) => {
        assert(!isInaccessible(savedResultsDataFrameGQL), 'User does not have access to step output');

        const savedResultsDataFrame = savedResultsDataFrameGQL.dataFrameJson as DataFrame;

        const resultAccessor = stringAccessor(
          createAccessor(savedResultsDataFrame, 'result', SAVED_RESULT_VALIDATION_OPTIONS).accessor
        );
        const resultIds = savedResultsDataFrame.rows.map((row) => resultAccessor(row));

        // TODO (BNCH-54762) Add pagination for result sets with more than `row_limit` rows
        return (
          <BenchlingQuery<GetSavedResultsQuery, GetSavedResultsQueryVariables>
            query={GET_SAVED_RESULTS}
            variables={{resultIds, schemaId: resultSchemaId}}
            renderLoading={() => <CenteredHorizontalThrobber />}
          >
            {(resultsData, resultsResponse) => {
              if (resultsData.resultSchema == null || isInaccessible(resultsData.resultSchema)) {
                return (
                  <InlineBanner
                    bannerType={InlineBannerType.ERROR}
                    text={ERROR_INACCESSIBLE_RESULTS_SCHEMA_TEXT}
                  />
                );
              }

              const results = graphQLArrayToREST('Result', assertAllAccessible(resultsData.results));
              const resultSchema = graphQLToREST('ResultSchema', resultsData.resultSchema);

              return children(
                savedResultsDataFrameGQL.numRows,
                results,
                resultSchema,
                resultsResponse.refetch
              );
            }}
          </BenchlingQuery>
        );
      }}
    </BenchlingQuery>
  );
}
